import { generateFormJson } from "./formJsonBuilder";
import {
  isRawFormType,
  buildIntegrationScaffold,
  RAW_TEMPLATE_KEY,
  RAW_INTEGRATION_KEY,
} from "./rawFormTypes";
import { sanitizeAdditionalPlacements } from "./additionalFieldLink";
import { deduplicateSectionsByAttributeName, normalizeSignatures } from "./studioStateNormalizers";
import { resolveSignerAttributeBindingsForLoad, buildSignaturesTemplate } from "./signatureUtils";

// Port of the force-gcs script's buildLoadedState: normalize a parsed
// StudioFormData revision (getFormDataFromCloudById -> parseStudioFormData)
// into the editor-shaped state that generateFormJson expects. Mirrors the
// transforms doLoadFromCloud applies, so a re-generated publish JSON matches
// what the single-form publish would produce.
function buildLoadedState(loaded) {
  const dl = deduplicateSectionsByAttributeName(loaded.sections || []);
  const st = {
    fields: (loaded.fields || []).map((f) => ({
      ...f,
      sectionId: dl.sectionIdMap[f.sectionId] ?? f.sectionId,
    })),
    sections: dl.sections.length ? dl.sections : loaded.sections || [],
    formDetails: loaded.formDetails || {},
    signatures: {},
    signerAttributeBindings: loaded.signerAttributeBindings || [],
    selectedCreators: loaded.selectedCreators || [],
    formUploadSections: loaded.formUploadSections || [],
    signatureAnnotations: loaded.signatureAnnotations || [],
    initialsAnnotations: loaded.initialsAnnotations || [],
    extraFormSummary: loaded.extraFormSummary || {},
    extraDealSummary: loaded.extraDealSummary || {},
    rulesJSON: loaded.rulesJSON ?? null,
    initials: loaded.initials ?? null,
    additionalFields: sanitizeAdditionalPlacements(loaded.additionalFields ?? [], loaded),
    initialerAttributeBindings: loaded.initialerAttributeBindings || [],
    subsequentSigning: loaded.subsequentSigning || {},
    subsequentSigningAttributeBindings: loaded.subsequentSigningAttributeBindings || [],
  };
  if (loaded.signatures && Object.keys(loaded.signatures).length > 0) {
    const { signatures: sigStripped, bindings } = resolveSignerAttributeBindingsForLoad(
      loaded.signatures,
      loaded.signerAttributeBindings,
    );
    st.signatures = normalizeSignatures(sigStripped);
    st.signerAttributeBindings = bindings;
  } else if (loaded.selectedRoles && loaded.selectedRoles.length > 0) {
    const tmpl = buildSignaturesTemplate();
    st.signatures = normalizeSignatures(
      Object.fromEntries(loaded.selectedRoles.map((k) => [k, tmpl[k]]).filter(([, v]) => !!v)),
    );
  } else {
    st.signatures = normalizeSignatures({});
  }
  return st;
}

// Port of the script's buildPublishArgs.
function buildPublishArgs(st, version) {
  return {
    fields: st.fields,
    sections: st.sections,
    signatures: st.signatures,
    selectedCreators: st.selectedCreators,
    formDetails: { ...st.formDetails, version },
    formUploadSections: st.formUploadSections,
    signatureAnnotations: st.signatureAnnotations,
    initialsAnnotations: st.initialsAnnotations,
    additionalFields: st.additionalFields,
    signerAttributeBindings: st.signerAttributeBindings,
    initialerAttributeBindings: st.initialerAttributeBindings,
    subsequentSigning: st.subsequentSigning,
    subsequentSigningAttributeBindings: st.subsequentSigningAttributeBindings,
    isPurchaseContract: st.formDetails?.settings?.type?.type === "isPurchaseContract",
    extraFormSummary: st.extraFormSummary,
    extraDealSummary: st.extraDealSummary,
    rulesJSON: st.rulesJSON,
    initials: st.initials,
  };
}

// Split the persisted rawFormJSON blob into { template, integration } objects
// (same shape App's splitRawFormBlob produces).
function splitRawBlob(blob) {
  const template = (blob && blob[RAW_TEMPLATE_KEY]) || blob;
  const integration = (blob && blob[RAW_INTEGRATION_KEY]) || buildIntegrationScaffold(template);
  return { template, integration };
}

/**
 * buildRevisionPublishPayload — from a parsed StudioFormData revision (the
 * getFormDataFromCloudById result), build the publish payload exactly as the
 * single-form publish would:
 *   - raw / mevi-sign forms: publish the verbatim rawFormJSON template plus the
 *     companion integration entry (the script omits both — this matches the UI).
 *   - normal PDF forms: regenerate via generateFormJson from the loaded state.
 * Version is the revision's own version (publish-only, no bump).
 * Returns { version, name, json, integrationJson|null }.
 */
export function buildRevisionPublishPayload(data) {
  const formDetails = data.formDetails || {};
  const version = formDetails.version || "";
  const name = data.name || formDetails.shortName || "";

  if (isRawFormType(formDetails.type) && data.rawFormJSON) {
    const { template, integration } = splitRawBlob(data.rawFormJSON);
    return {
      version,
      name,
      json: { ...template, version },
      integrationJson: { ...integration, version },
    };
  }

  const state = buildLoadedState(data);
  const json = JSON.parse(generateFormJson(buildPublishArgs(state, version)));
  return { version, name, json, integrationJson: null };
}
